// src/pages/CreateGroup.js
import React, { useRef, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  CalendarMonthOutlined,
  CloudUploadOutlined,
  PaymentsOutlined,
  AccountBalanceWalletOutlined,
  GroupAddOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { uploadData } from "../services/request";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#8e62a4" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#52057b",
      borderWidth: 2,
      boxShadow: "0 4px 12px rgba(82, 5, 123, 0.1)",
    },
  },
};

const CreateGroup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [groupTitle, setGroupTitle] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [months, setMonths] = useState("");
  const [duration, setDuration] = useState("monthly");
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [insuranceHtml, setInsuranceHtml] = useState("");
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    text: "",
    severity: "success",
  });

  // Validation states for real-time feedback
  const [amountTouched, setAmountTouched] = useState(false);
  const [monthsTouched, setMonthsTouched] = useState(false);

  const goTo = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  // ✅ Page Mount Hote Hi Insurance Check
  useEffect(() => {
    const storedUserData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (Number(storedUserData?.isInsured) === 0) {
      const nin = JSON.parse(localStorage.getItem("nin") || "{}") || {};
      const ninValue = nin?.nin || nin?.data?.nin || "";
      navigate(`/insurance?nin=${encodeURIComponent(ninValue)}`, {
        replace: true,
      });
    }
  }, [navigate]);

  // ✅ Calculated Monthly Fee
  const calculatedFee = amount && months ? Number(amount) / Number(months) : 0;

  // ✅ Minimum monthly fee: 5000 NGN
  const MIN_MONTHLY_FEE = 5000;
  const isFeeValid = calculatedFee >= MIN_MONTHLY_FEE;

  // Get error message for monthly fee
  const getFeeError = () => {
    if (!amountTouched || !monthsTouched) return "";
    if (calculatedFee === 0) return "";
    if (!isFeeValid) {
      return `Monthly fee must be at least NGN ${MIN_MONTHLY_FEE.toLocaleString()}. Current: NGN ${calculatedFee.toFixed(2)}`;
    }
    return "";
  };

  // Check if total amount is sufficient
  const isTotalAmountValid = amount && Number(amount) >= MIN_MONTHLY_FEE;

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      setImage({ preview: reader.result, name: file.name, file });
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // ✅ Submit ke waqt bhi check (safety ke liye)
    const storedUserData = JSON.parse(localStorage.getItem("userData") || "{}");
    if (Number(storedUserData?.isInsured) === 0) {
      const nin = JSON.parse(localStorage.getItem("nin") || "{}") || {};
      const ninValue = nin?.nin || nin?.data?.nin || "";
      navigate(`/insurance?nin=${encodeURIComponent(ninValue)}`, {
        replace: true,
      });
      return;
    }

    if (!groupTitle.trim() || !description.trim() || !amount || !months) {
      toast.error("Please complete all required fields.");
      return;
    }

    // ✅ Validation: Monthly fee must be at least 5000
    if (calculatedFee < MIN_MONTHLY_FEE) {
      toast.error(
        `Monthly fee must be at least NGN ${MIN_MONTHLY_FEE.toLocaleString()}.`,
      );
      return;
    }

    // ✅ Validation: Total amount must be at least 5000
    if (Number(amount) < MIN_MONTHLY_FEE) {
      toast.error(
        `Total amount must be at least NGN ${MIN_MONTHLY_FEE.toLocaleString()}.`,
      );
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", groupTitle);
      formData.append("description", description);
      formData.append("amount", amount);
      formData.append("fee", calculatedFee);
      formData.append("interval", duration);
      formData.append("duration", months);

      if (image?.file) {
        formData.append("groupImage", image.file);
      }

      const response = await uploadData(
        "/api/group/create",
        formData,
        localStorage.getItem("accessToken"),
      );

      if (response?.status === true) {
        toast.success("Group created successfully!");
        setTimeout(() => navigate("/home"), 1500);
      } else {
        toast.error(response?.message || "Failed to create group.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f6f7fb" }}>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            borderRadius: "12px",
            background: "#fff",
            padding: "16px 20px",
            boxShadow: "0px 10px 30px rgba(0,0,0,0.1)",
          },
          success: {
            iconTheme: {
              primary: "#52057b",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#d32f2f",
              secondary: "#fff",
            },
          },
        }}
      />

      {/* Navbar */}
      <Navbar
        onMenuClick={() => setMobileNavOpen(true)}
        onNavigate={goTo}
        onLogout={() => {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("userData");
          localStorage.removeItem("deviceId");
          localStorage.removeItem("notifications");
          navigate("/", { replace: true });
        }}
        user={{ name: "John Doe", role: "Member" }}
      />

      {/* Fixed Sidebar + Main Content */}
      <Box sx={{ display: "flex", pt: { md: "40px" } }}>
        <Sidebar
          activePath={location.pathname}
          onNavigate={goTo}
          mobileOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ maxWidth: 900, mx: "auto" }}>
            <>
              {showInsuranceForm ? (
                <Paper
                  sx={{
                    p: { xs: 2, sm: 3 },
                    border: "1px solid #e9ebf0",
                    borderRadius: "20px",
                    backgroundColor: "#fff",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setShowInsuranceForm(false)}
                    sx={{
                      mb: 2,
                      borderColor: "#52057b",
                      color: "#52057b",
                      textTransform: "none",
                    }}
                  >
                    Back to group form
                  </Button>
                  <Box
                    sx={{
                      borderRadius: "16px",
                      overflow: "hidden",
                      border: "1px solid #e9ebf0",
                    }}
                  >
                    <iframe
                      title="Insurance Declaration Form"
                      srcDoc={insuranceHtml}
                      style={{
                        width: "100%",
                        minHeight: "1100px",
                        border: "none",
                        display: "block",
                        background: "#fff",
                      }}
                    />
                  </Box>
                </Paper>
              ) : (
                <Paper
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    p: { xs: 2.5, sm: 4, md: 5 },
                    border: "1px solid #e9ebf0",
                    borderRadius: "20px",
                    boxShadow: "0 12px 35px rgba(28, 32, 50, 0.06)",
                    backgroundColor: "#fff",
                  }}
                >
                  {/* Intro */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 800,
                        color: "#1d2433",
                        mb: 0.6,
                      }}
                    >
                      Group details
                    </Typography>
                    <Typography sx={{ color: "#7a8394", fontSize: 14 }}>
                      Set up the savings group and invite people to reach a
                      shared goal.
                    </Typography>
                  </Box>

                  {/* Group Title & Image (Grid) */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 200px" },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <FieldLabel text="Group title" required />
                      <TextField
                        fullWidth
                        placeholder="e.g. Family savings circle"
                        value={groupTitle}
                        onChange={(event) => setGroupTitle(event.target.value)}
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <GroupAddOutlined sx={{ color: "#7a8394" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {/* Image Upload Box */}
                    <Box>
                      <FieldLabel text="Group image" />
                      <Box
                        component="button"
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          width: "100%",
                          height: "56px",
                          border: "1.5px dashed #b8a7c2",
                          borderRadius: "12px",
                          backgroundColor: "#fcfbfd",
                          color: "#52057b",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          overflow: "hidden",
                          p: 1,
                          "&:hover": {
                            borderColor: "#52057b",
                            backgroundColor: "#f9f4fb",
                          },
                        }}
                      >
                        <input
                          ref={fileInputRef}
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                        {image ? (
                          <>
                            <Box
                              component="img"
                              src={image.preview}
                              alt="Group preview"
                              sx={{
                                width: "100%",
                                height: "38px",
                                objectFit: "cover",
                                borderRadius: "6px",
                              }}
                            />
                            <Typography
                              sx={{ fontSize: 10, color: "#7a8394", mt: 0.5 }}
                            >
                              {image.name.slice(0, 15)}...
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CloudUploadOutlined
                              sx={{ fontSize: 22, mb: 0.5 }}
                            />
                            <Typography sx={{ fontWeight: 700, fontSize: 11 }}>
                              Upload
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Description */}
                  <FieldLabel
                    text="Group description"
                    required
                    sx={{ mt: 2.5 }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={6}
                    placeholder="Tell members what this group is for"
                    value={description}
                    onChange={(event) =>
                      setDescription(event.target.value.slice(0, 200))
                    }
                    helperText={`${description.length}/200`}
                    sx={{
                      ...fieldSx,
                      "& .MuiFormHelperText-root": {
                        textAlign: "right",
                        mr: 0,
                        color: "#8b93a2",
                      },
                    }}
                  />

                  {/* Amount & Months */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                      mt: 2.5,
                    }}
                  >
                    <Box>
                      <FieldLabel text="Amount (NGN)" required />
                      <TextField
                        fullWidth
                        type="number"
                        placeholder="0.00"
                        value={amount}
                        onChange={(event) => setAmount(event.target.value)}
                        onBlur={() => setAmountTouched(true)}
                        error={
                          amountTouched && !isTotalAmountValid && amount !== ""
                        }
                        helperText={
                          amountTouched && !isTotalAmountValid && amount !== ""
                            ? `Amount must be at least NGN ${MIN_MONTHLY_FEE.toLocaleString()}`
                            : ""
                        }
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccountBalanceWalletOutlined
                                sx={{ color: "#7a8394" }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">NGN</InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <Box>
                      <FieldLabel text="Number of months" required />
                      <TextField
                        fullWidth
                        type="number"
                        placeholder="e.g. 12"
                        value={months}
                        onChange={(event) => setMonths(event.target.value)}
                        onBlur={() => setMonthsTouched(true)}
                        sx={fieldSx}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              months
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Monthly Fee & Duration */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                      mt: 2.5,
                    }}
                  >
                    <Box>
                      <FieldLabel
                        text={`Monthly fee (Min: NGN ${MIN_MONTHLY_FEE.toLocaleString()})`}
                        required
                      />
                      <TextField
                        fullWidth
                        value={calculatedFee.toFixed(2)}
                        placeholder="Calculated automatically"
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              NGN
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          ...fieldSx,
                          "& .MuiOutlinedInput-root": {
                            ...fieldSx["& .MuiOutlinedInput-root"],
                            backgroundColor: "#f8f6f9",
                          },
                        }}
                        error={!isFeeValid && calculatedFee > 0}
                        helperText={
                          !isFeeValid && calculatedFee > 0
                            ? `Monthly fee must be at least NGN ${MIN_MONTHLY_FEE.toLocaleString()}. Current: NGN ${calculatedFee.toFixed(2)}`
                            : calculatedFee > 0 && isFeeValid
                              ? `✅ Valid: NGN ${calculatedFee.toFixed(2)} per month`
                              : ""
                        }
                        FormHelperTextProps={{
                          sx: {
                            color:
                              !isFeeValid && calculatedFee > 0
                                ? "#d32f2f"
                                : "#2e7d32",
                            fontWeight: 500,
                          },
                        }}
                      />
                    </Box>
                    <Box>
                      <FieldLabel text="Duration" />
                      <TextField
                        select
                        fullWidth
                        value={duration}
                        disabled
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarMonthOutlined
                                sx={{ color: "#7a8394", fontSize: 20 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      >
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </TextField>
                    </Box>
                  </Box>

                  {/* Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1.5,
                      mt: 4,
                      flexDirection: { xs: "column-reverse", sm: "row" },
                    }}
                  >
                    <Button
                      type="button"
                      onClick={() => navigate("/home")}
                      sx={{
                        color: "#687284",
                        textTransform: "none",
                        fontWeight: 700,
                        px: 3,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading || !isFeeValid || !isTotalAmountValid}
                      startIcon={
                        loading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <PaymentsOutlined />
                        )
                      }
                      sx={{
                        backgroundColor: "#52057b",
                        borderRadius: "12px",
                        px: 3,
                        py: 1.3,
                        textTransform: "none",
                        fontWeight: 750,
                        boxShadow: "0 6px 20px rgba(82, 5, 123, 0.25)",
                        "&:hover": {
                          backgroundColor: "#3e055e",
                          boxShadow: "0 8px 25px rgba(82, 5, 123, 0.3)",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#e0e0e0",
                          color: "#9e9e9e",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {loading ? "Creating..." : "Create group"}
                    </Button>
                  </Box>
                </Paper>
              )}
            </>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={message.open}
        autoHideDuration={4000}
        onClose={() => setMessage({ ...message, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={message.severity}
          onClose={() => setMessage({ ...message, open: false })}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const FieldLabel = ({ text, required, sx = {} }) => (
  <Typography
    component="label"
    sx={{
      display: "block",
      color: "#3f2949",
      fontSize: 13,
      fontWeight: 750,
      mb: 0.8,
      ...sx,
    }}
  >
    {text}
    {required && (
      <Box component="span" sx={{ color: "#c0392b", ml: 0.3 }}>
        *
      </Box>
    )}
  </Typography>
);

export default CreateGroup;
